import React, { useEffect, useMemo, useState } from "react";
import CardLayout from "../../components/utils/CardLayout";
import Button from "../../components/utils/Button";
import { useDispatch, useSelector } from "react-redux";
import genericActions from "../../redux/generic/actions";
import authActions from "../../redux/auth/actions";
import { useForm } from "react-hook-form";
import ToastAlert from "../../components/utils/ToastAlert";
import { twoDigitDecimal } from '../../helpers/commonFunction';
import Table from "../../components/utils/Table";
import moment from "moment";
import { useNavigate } from 'react-router-dom';

const dateFormat = process.env.REACT_APP_DATE_TIME_FORMAT;

const { getACode } = genericActions;
const { fundManagement, resetMessage, getUserDataByToken, getLatestfundManagementData } = authActions;
const maxAmount=2500;

const FundManagement = () => {
    const dispatch = useDispatch();
    const { aCodeList, getACodeLoading } = useSelector((state) => state.generic);
    const { fundManagementLoading, fundManagementMessage, fundManagementError, defaultCurrency, getLatestFundManagementList } = useSelector((state) => state.auth)
    const [actionTypeFlag, setActionTypeFlag] = useState('')
    const navigate = useNavigate();


    const { register, handleSubmit, reset, watch, setValue, formState: { errors }, } = useForm({
        mode: "onChange",
        defaultValues: { dealerId: "", amount: "", remarks: "", },
    });

    const watchACode = watch("dealerId");
    const selectedAcode = aCodeList?.find((item) => Number(item?.Id) === Number(watchACode));
    const dealerAmount = selectedAcode?.AccountBalance ?? 0;

    useEffect(() => {
        dispatch(getACode());
        dispatch(getLatestfundManagementData());
        return () => {
            dispatch(resetMessage());
        };
    }, [dispatch]);

    useEffect(() => {
        if (watchACode) {
            setValue("dealerId", watchACode)
        }
    }, [watchACode, aCodeList]);

    useEffect(() => {
        if (fundManagementMessage) {
            ToastAlert({ type: "success", text: fundManagementMessage })
            dispatch(getUserDataByToken())
            dispatch(getACode());
            dispatch(getLatestfundManagementData());

            setValue("amount", "");
            setValue("remarks", "");
            setActionTypeFlag('');

        }

        if (fundManagementError) {
            ToastAlert({ type: "error", text: fundManagementError })
            setActionTypeFlag('')
        }
    }, [fundManagementMessage, fundManagementError])

    const handleFundSubmit = (data, actionType) => {
        const amount = Number(data?.amount) || 0;

        if (!amount || amount == 0) {
            ToastAlert({ type: "error", text: "Amount must be greater than zero.", })
            return;
        }

        if (actionType === 1 && amount > maxAmount) {
            ToastAlert({
                type: "error",
                text: `Maximum amount  allowed for Add fund is $${maxAmount}.`,
            })
            return;
        }
        if (actionType === 2 && amount > dealerAmount) {
            ToastAlert({
                type: "error",
                text: `Insufficient balance. Available balance is ${defaultCurrency}${twoDigitDecimal(dealerAmount)}.`,
            });
            return;
        }
        setActionTypeFlag(actionType);
        const payload = {
            dealerId: Number(data?.dealerId) || 0,
            amount: Number(data?.amount) || 0,
            actionType,
            remarks: data?.remarks || "",
        };
        dispatch(fundManagement(payload));
    };

    const columns = [
        { displayName: "ACode", key: "aCode" },
        { displayName: "Transaction Amount", key: "txnAmount" },
        { displayName: "Transaction Date", key: "transactionDate" },
        { displayName: "Transaction Type", key: "transactionType" },
        { displayName: "Status", key: "status" },
        { displayName: "Remarks", key: "remark" },
    ];


    const rowData = useMemo(() => {
        return (getLatestFundManagementList || []).filter((item, ind) => ind < 10).map((item) => {
            return {
                TxnId: item?.TransactionID,
                aCode: item?.ACode,
                txnAmount: defaultCurrency + '' + item?.TxnAmount,
                transactionDate: moment(item?.TransactionDate).format(dateFormat),
                transactionType: item?.TransactionType,
                status: ( <span className={`badge ${item?.Status.toLowerCase() === "success" ? "bg-success" : "bg-danger"}`}>{item?.Status}</span>),
                remark: item?.Remarks || '-',
            };
        });
    }, [getLatestFundManagementList, defaultCurrency]);


    const handleViewAll = () => {
        navigate("/reports/fund-report");
    }


    return (
        <CardLayout
            title="Fund Management "
            icon="fas fa-hand-holding-usd" >
            <div className="row g-3">
                {/* Fund Management */}
                <div className="col-md-4">
                    <div className="bg-white rounded-3 border h-100">
                        <form onSubmit={handleSubmit(handleFundSubmit)} className="card border-0 h-100">

                            {/* Header */}
                            <div className="fm-header rounded-top-3">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            color: "rgb(230, 211, 211)",
                                            fontWeight: "600",
                                        }}
                                    >
                                        Current Balance
                                    </div>

                                    <span className="fm-balance">
                                        {defaultCurrency}
                                        {twoDigitDecimal(dealerAmount)}
                                    </span>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="card-body">
                                <label className="form-label fw-semibold">
                                    ACode <span className="text-danger">*</span>
                                </label>

                                <select
                                    className={`form-select mb-2 ${errors.dealerId ? "is-invalid" : ""
                                        }`}
                                    {...register("dealerId", {
                                        required: "ACode is required",
                                    })}
                                    disabled={getACodeLoading}
                                >
                                    <option value="">Select ACode</option>

                                    {aCodeList
                                        ?.filter((item) => Number(item?.Id) !== 1)
                                        ?.map((item) => (
                                            <option key={item?.Id} value={item?.Id}>
                                                 {item?.ACode} ({item?.DealerName})
                                            </option>
                                        ))}
                                </select>

                                {errors.dealerId && (
                                    <div className="invalid-feedback">
                                        {errors.dealerId.message}
                                    </div>
                                )}

                                <label className="form-label fw-semibold">
                                    Amount <span className="text-danger">*</span>
                                </label>

                                <div className="input-group mb-2">
                                    <span className="input-group-text text-primary">
                                        {defaultCurrency}
                                    </span>

                                    <input
                                        type="text"
                                        className={`form-control ${errors.amount ? "is-invalid" : ""
                                            }`}
                                        placeholder="0.00"
                                        {...register("amount", {
                                            required: "Amount is required",
                                            validate: (value) =>
                                                /^\d+(\.\d{1,2})?$/.test(value) ||
                                                "Enter a valid amount",
                                        })}
                                        onInput={(e) => {
                                            e.target.value = e.target.value
                                                .replace(/[^0-9.]/g, "")
                                                .replace(/(\..*)\./g, "$1")
                                                .replace(/^(\d+\.\d{0,2}).*$/, "$1");
                                        }}
                                    />

                                    {errors.amount && (
                                        <div className="invalid-feedback">
                                            {errors.amount.message}
                                        </div>
                                    )}
                                </div>

                                <label className="form-label fw-semibold">
                                    Remarks
                                </label>

                                <textarea
                                    rows="4"
                                    style={{ resize: "none" }}
                                    {...register("remarks")}
                                    className="form-control shadow-none mb-2"
                                    placeholder="Add a note for this transaction"
                                />

                                <div className="fm-actions">
                                    <Button
                                        title="Deduct funds"
                                        buttonType="submit"
                                        loading={
                                            actionTypeFlag === 2 &&
                                            fundManagementLoading
                                        }
                                        variant="secondary"
                                        icon={
                                            <i className="ri-arrow-down-circle-line me-1"></i>
                                        }
                                        onClick={handleSubmit((data) =>
                                            handleFundSubmit(data, 2)
                                        )}
                                        disable={fundManagementLoading}
                                    />

                                    <Button
                                        title="Add funds"
                                        buttonType="submit"
                                        loading={
                                            actionTypeFlag === 1 &&
                                            fundManagementLoading
                                        }
                                        variant="primary"
                                        icon={
                                            <i className="ri-arrow-up-circle-line me-1"></i>
                                        }
                                        onClick={handleSubmit((data) =>
                                            handleFundSubmit(data, 1)
                                        )}
                                        disable={fundManagementLoading}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Latest Transactions */}
                <div className="col-md-8">
                    <div className="bg-white border rounded-3 h-100">
                        <div className="card-header bg-transparent d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 title">
                                Latest Transactions
                            </h6>

                            <Button
                                title="View All"
                                buttonType="button"
                                variant="primary"
                                icon={<i className="ri-eye-line me-1"></i>}
                                onClick={handleViewAll}
                                style={{ padding: "4px 10px" }}
                            />
                        </div>

                        <div className="px-3 py-2">
                            <Table
                                columns={columns}
                                data={rowData}
                                pagination={false}
                                tableHeight="400px"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </CardLayout >
    );
};

export default FundManagement;
